<script setup lang="ts">
import { getCurrentLocale } from '@/utils/helpers'

const route = useRoute()
const i18n = useI18n()
const isMobileNavOpen = ref(false)
const visibilityClass = computed(
  () => isMobileNavOpen.value && 'navigation-mobile__content--visible',
)

interface Props {
  navbarColor?: string
  isTransparent?: boolean
  buttonsColor?: '#000033' | '#fff'
}
const props = defineProps<Props>()

const {
  navbarStyle,
  isNavbarTransparent,
  isNavbarLight,
  isConfiguredTransparent,
} = useNavbarStyle(
  toRef(props, 'navbarColor'),
  toRef(props, 'isTransparent'),
  toRef(props, 'buttonsColor'),
)

const language = getCurrentLocale(i18n)

const handleCloseNavigation = (path?: string) => {
  if (!path || route.path === `${language}${path}`) {
    setIsOpen(false)
  }
}

const setIsOpen = (payload: boolean) => {
  isMobileNavOpen.value = payload
}

watch(
  () => route.fullPath,
  () => {
    isMobileNavOpen.value = false
  },
)
watch(isMobileNavOpen, () => {
  document.documentElement.style.overflow = isMobileNavOpen.value
    ? 'hidden'
    : 'auto'
})
onUnmounted(() => {
  document.documentElement.style.overflow = 'auto'
})
</script>

<template>
  <nav
    :class="[
      'navigation-mobile',
      {
        'navigation-mobile--transparent': isConfiguredTransparent,
        'navigation-mobile--scrolled':
          isConfiguredTransparent && !isNavbarTransparent,
      },
    ]"
    :style="navbarStyle"
  >
    <header class="navigation-mobile__header container">
      <NavigationLogo
        :is-white="isNavbarLight"
        :is-transparent="isNavbarTransparent"
      />
      <div class="navigation-mobile__header-actions">
        <BaseButton
          class="navigation-mobile__cta-button"
          small
          :title="$t('common.navigation.cta')"
          :href="{ cached_url: `${language}/contact-sales` }"
        >
          <template #text>
            {{ $t('common.navigation.cta') }}
          </template>
        </BaseButton>
        <NavigationBurgerButton
          :aria-expanded="isMobileNavOpen.toString()"
          :navbar-color="navbarColor"
          :is-transparent="isTransparent"
          :buttons-color="buttonsColor"
          class="navigation-mobile__button"
          @click="setIsOpen(true)"
        />
      </div>
    </header>
    <NavigationMobileContent
      v-if="isMobileNavOpen"
      id="primary-navigation-mobile"
      :is-open="isMobileNavOpen"
      :class="['navigation-mobile__content', visibilityClass]"
      @close="handleCloseNavigation"
    />
  </nav>
</template>

<style lang="scss" scoped>
.container {
  @media (min-width: $breakpoint-md) {
    --container-padding: 54px;
  }
}

.navigation-mobile {
  position: relative;
  background-color: var(--navbar-solid-bg, var(--white));
  color: var(--navbar-solid-text, var(--cobalt-800));
  position: sticky;
  top: 0;
  z-index: 9998;
  transition:
    background-color 0.35s ease,
    color 0.35s ease,
    box-shadow 0.35s ease;

  &--transparent:not(&--scrolled) {
    background-color: transparent;
    color: var(--navbar-overlay-text, var(--white));
  }

  &--transparent {
    margin-bottom: calc(-1 * var(--header-height));
  }

  &--scrolled {
    @include shadow-flat-soft;
  }

  @media (min-width: $breakpoint-lg) {
    display: none;
  }

  &__header {
    transition:
      background-color 0.35s ease,
      color 0.35s ease;
    height: var(--header-height);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: inherit;
    color: inherit;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__cta-button {
    display: none;

    @media (min-width: $breakpoint-md) and (max-width: #{$breakpoint-lg - 1px}) {
      display: inline-flex;
    }
  }

  &__button {
    position: relative;
    padding: 15px 10px;
    transform: translateX(10px);
  }

  &__content {
    display: none;

    &--visible {
      display: block;
    }
  }
}
</style>
